const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Safely parse response — returns parsed JSON or falls back to a plain error object
const parseResponse = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { error: `Server error (${res.status})` };
  }
};

const leadsService = {
  async getLeads(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    const res = await fetch(`${API_URL}/leads${qs ? '?' + qs : ''}`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to fetch leads');
    return { success: true, data: json };
  },

  async getLead(id) {
    const res = await fetch(`${API_URL}/leads/${id}`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to fetch lead');
    return { success: true, data: json };
  },

  async createLead(data) {
    const res = await fetch(`${API_URL}/leads`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data),
    });
    const json = await parseResponse(res);
    if (!res.ok) return { success: false, status: res.status, ...json };
    return { success: true, data: json };
  },

  async updateLead(id, data) {
    const res = await fetch(`${API_URL}/leads/${id}`, {
      method: 'PATCH', headers: getHeaders(), body: JSON.stringify(data),
    });
    const json = await parseResponse(res);
    if (!res.ok) return { success: false, status: res.status, ...json };
    return { success: true, data: json };
  },

  async updateStatus(id, status, notes = '', lost_reason = '') {
    const res = await fetch(`${API_URL}/leads/${id}/status`, {
      method: 'PATCH', headers: getHeaders(),
      body: JSON.stringify({ status, notes, lost_reason }),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to update status');
    return { success: true, data: json };
  },

  async resendPayment(id, delivery_date, delivery_window) {
    const res = await fetch(`${API_URL}/leads/${id}/resend-payment`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ delivery_date, delivery_window }),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to resend payment link');
    return { success: true, data: json };
  },

  async getInteractions(id) {
    const res = await fetch(`${API_URL}/leads/${id}/interactions`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to fetch interactions');
    return { success: true, data: json };
  },

  async logInteraction(id, data) {
    const res = await fetch(`${API_URL}/leads/${id}/interactions`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to log interaction');
    return { success: true, data: json };
  },

  async convert(id, payload) {
    const res = await fetch(`${API_URL}/leads/${id}/convert`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(payload),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to convert lead');
    return { success: true, data: json };
  },

  async getFormMappings(formId) {
    const res = await fetch(`${API_URL}/lead-import/form-mappings/${encodeURIComponent(formId)}`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to fetch form mappings');
    return { success: true, data: json };
  },

  async saveFormMappings(formId, formName, mappings) {
    const res = await fetch(`${API_URL}/lead-import/form-mappings`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ form_id: formId, form_name: formName, mappings }),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to save form mappings');
    return { success: true };
  },

  async validateImportPhones(phones, pincodes = []) {
    const res = await fetch(`${API_URL}/lead-import/validate`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify({ phones, pincodes }),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to validate phones');
    return { success: true, data: json };
  },

  async importLeads(payload) {
    const res = await fetch(`${API_URL}/lead-import/import`, {
      method: 'POST', headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to import leads');
    return { success: true, data: json };
  },

  async getImportHistory() {
    const res = await fetch(`${API_URL}/lead-import/history`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to fetch import history');
    return { success: true, data: json };
  },

  async getUsersWithLeadsRead() {
    const res = await fetch(`${API_URL}/leads/users-with-leads-read`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to fetch users');
    return { success: true, data: json };
  },

  async assignLead(id, assignedToId) {
    const res = await fetch(`${API_URL}/leads/${id}/assign`, {
      method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ assigned_to: assignedToId }),
    });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to assign lead');
    return { success: true, data: json };
  },

  async searchMenuProducts(search) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    const res = await fetch(`${API_URL}/leads/menu-products${qs}`, { headers: getHeaders() });
    const json = await parseResponse(res);
    if (!res.ok) throw new Error(json.error || 'Failed to search products');
    return json;
  },
};

export default leadsService;
